import {ChangeDetectorRef, Component, HostListener, Inject, Input} from '@angular/core';
import {faGlobe, faInfoCircle, faSyncAlt, faTag, faTv} from '@fortawesome/pro-solid-svg-icons';
import {ObservationTarget, StateObservationTarget} from '@webmate-web-core/shared/types/testmgmt';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {BrowserSessionStateArtifact} from '@webmate-web-core/shared/types/browsersession';
import {take} from 'rxjs/operators';
import {INTERACTION_PATH_OPENER, InteractionPathOpener} from '@webmate-web-core/shared/services/interactionpath-opener.service';

@Component({
    selector: 'wm-observation-target',
    templateUrl: './observation-target.component.html',
    styleUrls: ['./observation-target.component.less'],
})
export class ObservationTargetComponent {
    constructor(
        private cdr: ChangeDetectorRef,
        private artifactService: ArtifactService,
        @Inject(INTERACTION_PATH_OPENER) private readonly interactionPathOpener: InteractionPathOpener
    ) {}

    @Input()
    target: ObservationTarget;

    @Input()
    showIcon: boolean = true;

    state?: BrowserSessionStateArtifact;

    isHovered: boolean = false;

    isError = false;

    @HostListener('mouseenter')
    onMouseEnter() {
      this.isHovered = true;
    }

    @HostListener('mouseleave')
    onMouseLeave() {
      this.isHovered = false;
    }


    ngOnInit() {
        this.update();
    }

    ngOnChanges() {
        this.update();
    }

    update() {
        this.state = undefined;
        this.isError = false;

        if (!this.target) {
            this.isError = true;
            this.cdr.markForCheck();
            return;
        }

        if (this.target.type === 'StateObservationTarget') {
            this.artifactService.getArtifact$((this.target as StateObservationTarget).stateId).pipe(take(1)).subscribe({
                next: (artifact) => {
                    try {
                        this.state = BrowserSessionStateArtifact.fromArtifact(artifact);
                    } catch (error) {
                        this.isError = true;
                    }
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    this.isError = true;
                    this.cdr.markForCheck();
                }
            });
        } else if (this.target.type === 'TagObservationTarget' || this.target.type === 'ContextObservationTarget' || this.target.type === 'URLObservationTarget') {
            this.cdr.markForCheck();
        } else {
            this.isError = true;
            this.cdr.markForCheck();
        }
    }

    openInBrowser() {
        const interactionPath = this.state?.provenance?.path;
        if (interactionPath) {
            this.interactionPathOpener.open({interactionPath});
        }
    }

    getTargetLabel(): string {
        if (!this.target) {
            return 'Unknown target';
        }

        if (this.target.type === 'StateObservationTarget') {
            return this.target.name || this.state?.name || 'Unknown target';
        }

        if (this.target.type === 'URLObservationTarget') {
            return this.target.name || (this.target as any).url || 'URL target';
        }

        return this.target.name || this.target.description || 'Context target';
    }

    getEnvironmentSummary(): string | undefined {
        const environment = this.target?.environment;
        if (!environment) {
            return undefined;
        }

        const parts: string[] = [];
        if (environment.vehicle?.type) {
            parts.push(`vehicle=${environment.vehicle.type}`);
        }
        if (environment.viewport?.width != null && environment.viewport?.height != null) {
            parts.push(`viewport=${environment.viewport.width}x${environment.viewport.height}`);
        }
        if (environment.packageId) {
            parts.push(`package=${environment.packageId}`);
        }
        if (environment.tags?.length) {
            parts.push(
              `tags=${environment.tags
                .map((tag: any) => {
                  const name = tag.name?.name || tag.name;
                  const value = tag.value?.value ?? '';
                  return value === '' ? name : name + '=' + value;
                })
                .join(', ')}`
            );
        }
        if (environment.attributes && Object.keys(environment.attributes).length > 0) {
            parts.push(`attributes=${JSON.stringify(environment.attributes)}`);
        }

        return parts.length ? parts.join(', ') : undefined;
    }

    protected readonly faSyncAlt = faSyncAlt;
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faGlobe = faGlobe;
    protected readonly faTag = faTag;
    protected readonly faTv = faTv;
}
